// ===========================================
// My Subscriptions Page (Parent)
// ===========================================
// View and manage subscription payment plans

import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  Alert,
  Skeleton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Autorenew as SubscriptionIcon,
  CalendarMonth as CalendarIcon,
  CreditCard as CardIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Cancel as CancelIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { formatUTCDate } from '../../utils/dateUtils';
import {
  useParentSubscriptions,
  useParentPauseSubscription,
  useParentResumeSubscription,
  useParentCancelSubscription,
} from '../../hooks/useSubscriptions';
import {
  Subscription,
  SubscriptionStatus,
} from '../../api/subscriptions.api';

// ===========================================
// CONSTANTS
// ===========================================

const STATUS_CONFIG: Record<SubscriptionStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  ACTIVE: { label: 'Active', color: 'success' },
  PAUSED: { label: 'Paused', color: 'warning' },
  PAST_DUE: { label: 'Past Due', color: 'error' },
  UNPAID: { label: 'Unpaid', color: 'error' },
  CANCELLED: { label: 'Cancelled', color: 'default' },
  COMPLETED: { label: 'Completed', color: 'info' },
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(num);
}

/**
 * Local-timezone formatter for real TIMESTAMPS (cancelledAt, pausedAt) — these are genuine
 * points-in-time and should display in the viewer's local zone. Date-ONLY fields
 * (startDate/endDate/nextBillingDate) use `formatUTCDate` instead so their stored calendar day
 * doesn't shift by the viewer's offset.
 */
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  return new Intl.DateTimeFormat('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateString));
}

/** Date-only subscription fields (@db.Date, UTC midnight) — show the stored day, never shifted. */
function formatDateOnly(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  return formatUTCDate(dateString, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ===========================================
// SUBSCRIPTION CARD COMPONENT
// ===========================================

interface SubscriptionCardProps {
  subscription: Subscription;
  onPause: (sub: Subscription) => void;
  onResume: (sub: Subscription) => void;
  onCancel: (sub: Subscription) => void;
}

function SubscriptionCard({
  subscription,
  onPause,
  onResume,
  onCancel,
}: SubscriptionCardProps) {
  const statusConfig = STATUS_CONFIG[subscription.status];
  const isActive = subscription.status === 'ACTIVE';
  const isPaused = subscription.status === 'PAUSED';
  const canManage = isActive || isPaused;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {subscription.paymentPlan?.name || 'Subscription'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subscription.term?.name || 'Current Term'}
            </Typography>
          </Box>
          <Chip
            label={statusConfig.label}
            color={statusConfig.color}
            size="small"
          />
        </Box>

        {/* Subscription Details */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(subscription.amount)}
            </Typography>
            {subscription.paymentPlan?.interval && (
              <Typography variant="caption" color="text.secondary">
                per {subscription.paymentPlan.interval.toLowerCase()}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Next Payment
            </Typography>
            <Typography variant="body1">
              {formatDateOnly(subscription.nextBillingDate)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Start Date
            </Typography>
            <Typography variant="body1">
              {formatDateOnly(subscription.startDate)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              End Date
            </Typography>
            <Typography variant="body1">
              {formatDateOnly(subscription.endDate)}
            </Typography>
          </Grid>
        </Grid>

        {/* Payment Plan Info */}
        {subscription.paymentPlan && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#FCF6E6', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Payment Plan Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2">
                  {subscription.paymentPlan.type}
                </Typography>
              </Grid>
              {subscription.paymentPlan.installments && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Installments
                  </Typography>
                  <Typography variant="body2">
                    {subscription.paymentPlan.installments} payments
                  </Typography>
                </Grid>
              )}
              {subscription.paymentPlan.discountPercent && parseFloat(subscription.paymentPlan.discountPercent) > 0 && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Discount
                  </Typography>
                  <Chip
                    label={`-${parseFloat(subscription.paymentPlan.discountPercent)}%`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        {canManage && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {isActive && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PauseIcon />}
                onClick={() => onPause(subscription)}
              >
                Pause
              </Button>
            )}
            {isPaused && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<ResumeIcon />}
                onClick={() => onResume(subscription)}
              >
                Resume
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => onCancel(subscription)}
            >
              Cancel Subscription
            </Button>
          </Stack>
        )}

        {/* Status Messages */}
        {subscription.status === 'PAST_DUE' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Your subscription payment is past due. Please update your payment method to avoid service interruption.
          </Alert>
        )}
        {subscription.status === 'CANCELLED' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This subscription was cancelled on {formatDate(subscription.cancelledAt)}.
          </Alert>
        )}
        {subscription.status === 'PAUSED' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This subscription is paused since {formatDate(subscription.pausedAt)}.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================
// SUMMARY CARD COMPONENT
// ===========================================

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  color?: string;
  icon?: React.ReactNode;
}

function SummaryCard({ title, value, subtitle, color = '#4580E4', icon }: SummaryCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {icon && (
            <Box
              sx={{
                bgcolor: `${color}20`,
                borderRadius: 2,
                p: 1.5,
                color: color,
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ===========================================
// PAUSE DIALOG COMPONENT
// ===========================================

interface PauseDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (resumeAt?: string) => void;
  loading: boolean;
}

function PauseDialog({ open, onClose, onConfirm, loading }: PauseDialogProps) {
  const [resumeAt, setResumeAt] = useState('');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pause Subscription</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Pausing your subscription will temporarily stop billing. You can resume at any time.
        </Typography>
        <TextField
          fullWidth
          type="date"
          label="Auto-resume date (optional)"
          value={resumeAt}
          onChange={(e) => setResumeAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          helperText="Leave empty to resume manually"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => onConfirm(resumeAt || undefined)}
          disabled={loading}
        >
          {loading ? 'Pausing...' : 'Pause Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// CANCEL DIALOG COMPONENT
// ===========================================

interface CancelDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (immediately: boolean, reason?: string) => void;
  loading: boolean;
}

function CancelDialog({ open, onClose, onConfirm, loading }: CancelDialogProps) {
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Subscription</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Are you sure you want to cancel your subscription? This action may affect your access to services.
        </Alert>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>When to cancel</InputLabel>
          <Select
            value={cancelImmediately ? 'immediately' : 'end_of_period'}
            label="When to cancel"
            onChange={(e) => setCancelImmediately(e.target.value === 'immediately')}
          >
            <MenuItem value="end_of_period">At end of billing period</MenuItem>
            <MenuItem value="immediately">Immediately</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason for cancellation (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Please let us know why you're cancelling..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Keep Subscription
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => onConfirm(cancelImmediately, reason || undefined)}
          disabled={loading}
        >
          {loading ? 'Cancelling...' : 'Cancel Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function MySubscriptionsPage() {
  // State
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  // Queries
  const { data: subscriptionsData, isLoading, error } = useParentSubscriptions();
  const subscriptions = subscriptionsData?.subscriptions ?? [];

  // Mutations
  const pauseMutation = useParentPauseSubscription();
  const resumeMutation = useParentResumeSubscription();
  const cancelMutation = useParentCancelSubscription();

  // Handlers
  const handlePause = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setPauseDialogOpen(true);
  };

  const handleResume = async (subscription: Subscription) => {
    try {
      await resumeMutation.mutateAsync(subscription.id);
    } catch {
      // Error handled by mutation
    }
  };

  const handleCancel = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setCancelDialogOpen(true);
  };

  const confirmPause = async (resumeAt?: string) => {
    if (selectedSubscription) {
      try {
        await pauseMutation.mutateAsync({
          id: selectedSubscription.id,
          data: resumeAt ? { resumeAt } : undefined,
        });
        setPauseDialogOpen(false);
        setSelectedSubscription(null);
      } catch {
        // Error handled by mutation
      }
    }
  };

  const confirmCancel = async (immediately: boolean, reason?: string) => {
    if (selectedSubscription) {
      try {
        await cancelMutation.mutateAsync({
          id: selectedSubscription.id,
          data: { cancelImmediately: immediately, reason },
        });
        setCancelDialogOpen(false);
        setSelectedSubscription(null);
      } catch {
        // Error handled by mutation
      }
    }
  };

  // Calculate summary stats
  const activeSubscriptions = subscriptions.filter((s) => s.status === 'ACTIVE');
  const monthlyTotal = activeSubscriptions.reduce(
    (sum, s) => sum + parseFloat(String(s.amount)),
    0
  );

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <PageHeader
          title="My Subscriptions"
          subtitle="View and manage your payment subscriptions"
        />
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rectangular" height={250} sx={{ mb: 2 }} />
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <PageHeader
          title="My Subscriptions"
          subtitle="View and manage your payment subscriptions"
        />
        <Alert severity="error">
          Failed to load subscriptions. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="My Subscriptions"
        subtitle="View and manage your payment subscriptions"
        breadcrumbs={[
          { label: 'Dashboard', path: '..' },
          { label: 'My Subscriptions' },
        ]}
      />

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            title="Active Subscriptions"
            value={activeSubscriptions.length.toString()}
            color="#4580E4"
            icon={<SubscriptionIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            title="Monthly Total"
            value={formatCurrency(monthlyTotal)}
            subtitle="estimated billing"
            color="#96DAC9"
            icon={<CardIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            title="Next Payment"
            value={
              activeSubscriptions[0]?.nextBillingDate
                ? formatDateOnly(activeSubscriptions[0].nextBillingDate)
                : 'N/A'
            }
            color="#FFCE00"
            icon={<CalendarIcon />}
          />
        </Grid>
      </Grid>

      {/* Subscriptions List */}
      {subscriptions.length > 0 ? (
        <>
          {/* Active Subscriptions */}
          {activeSubscriptions.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                Active Subscriptions
              </Typography>
              {activeSubscriptions.map((subscription) => (
                <SubscriptionCard
                  key={subscription.id}
                  subscription={subscription}
                  onPause={handlePause}
                  onResume={handleResume}
                  onCancel={handleCancel}
                />
              ))}
            </Box>
          )}

          {/* Other Subscriptions */}
          {subscriptions.filter((s) => s.status !== 'ACTIVE').length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom color="text.secondary">
                Other Subscriptions
              </Typography>
              {subscriptions
                .filter((s) => s.status !== 'ACTIVE')
                .map((subscription) => (
                  <SubscriptionCard
                    key={subscription.id}
                    subscription={subscription}
                    onPause={handlePause}
                    onResume={handleResume}
                    onCancel={handleCancel}
                  />
                ))}
            </Box>
          )}
        </>
      ) : (
        <Alert severity="info">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            No Subscriptions Yet
          </Typography>
          <Typography variant="body2">
            You don't have any active subscriptions. Contact your music school to set up a payment plan.
          </Typography>
        </Alert>
      )}

      {/* Dialogs */}
      <PauseDialog
        open={pauseDialogOpen}
        onClose={() => {
          setPauseDialogOpen(false);
          setSelectedSubscription(null);
        }}
        onConfirm={confirmPause}
        loading={pauseMutation.isPending}
      />

      <CancelDialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setSelectedSubscription(null);
        }}
        onConfirm={confirmCancel}
        loading={cancelMutation.isPending}
      />
    </Box>
  );
}
